import { fetchEntities } from 'actions/entities'
import debounce from 'debounce'
import dotProp from "dot-prop";
import React from 'react'
import ReactDOM from "react-dom";
import { connect } from 'react-redux'
import { Dropdown } from "semantic-ui-react";
import { ButtonOr } from "semantic-ui-react";
import { ButtonGroup } from "semantic-ui-react";
import { FormField } from "semantic-ui-react";
import { Form, Modal } from "semantic-ui-react";
import { Button } from "semantic-ui-react";
import { getProposedInvoice } from "../../actions/invoices";
import { getDossiers } from "../../selectors/dossiers";
import InvoiceForm from "./InvoiceForm";

class CreateInvoiceForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = { selection: null, invoiceId: null, loading: false, fetchedInvoiceId: null, searchTerm: '' }
    this.dropdownRef = React.createRef()
    this.menuElement = null
    this.scrollThreshold = 24
    this.loadingMore = false
    this.debouncedSearch = debounce((searchTerm) => {
      this.props.fetchFirstDossiersPage({ search: searchTerm })
    }, 350)
  }

  componentDidMount() {
    this.props.fetchFirstDossiersPage()
  }

  componentWillUnmount() {
    this.debouncedSearch.clear()
    this.unbindMenuScroll()
  }

  render() {
    const dossiers = this.props.dossiers

    const getDisplayName = (dossier) => dossier.patient && `${dossier.patient.firstName} ${dossier.patient.lastName}`
    const dossierOptions = dossiers && dossiers.map((dossier) => {
      return {
        key: dossier.id, value: dossier.id, content: getDisplayName(dossier),
        text: I18n.t('api.forms.invoice.new.from_dossier', {dossier: getDisplayName(dossier)})
      }
    })

    const selection = this.state.selection

    return (
      <Modal.Content>
        <Form loading={this.state.loading}>
          <FormField>
            <ButtonGroup>

              <Button primary={selection === 'empty'} onClick={this.handleSelectEmpty}>
                {I18n.t('api.forms.invoice.new.from_empty')}
              </Button>

              <ButtonOr text={I18n.t('generic.or')}/>

              <ButtonGroup color={selection === 'dossier' ? 'blue' : null}>
                <Dropdown button search style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                          options={dossierOptions}
                          onChange={this.handleSelectDossier}
                          onClose={this.handleDossierDropdownClose}
                          onOpen={this.handleDossierDropdownOpen}
                          onSearchChange={this.handleDossierSearchChange}
                          ref={this.dropdownRef}
                          placeholder={I18n.t('api.forms.invoice.new.from_dossier_placeholder')}/>
              </ButtonGroup>

            </ButtonGroup>
          </FormField>

          {selection && <InvoiceForm entityId={this.state.invoiceId} onSubmitSuccess={this.props.onSubmitSuccess} forProposedInvoice={selection==='dossier'}/>}

        </Form>
      </Modal.Content>
    )
  }

  handleDossierDropdownClose = (event, props) => {
    this.unbindMenuScroll()
    this.setState({searchTerm: ''})
    if (this.state.fetchedInvoiceId) {
      this.setState({ invoiceId: this.state.fetchedInvoiceId, selection: 'dossier' })
    }
  }

  handleDossierDropdownOpen = (event, props) => {
    this.props.fetchFirstDossiersPage()
    this.bindMenuScroll()
  }

  handleDossierSearchChange = (event, dropdownProps) => {
    const searchTerm = dropdownProps.searchQuery || ''
    this.setState({ searchTerm })
    this.debouncedSearch(searchTerm)
  }

  handleLoadMoreDossiers = () => {
    if (this.loadingMore || !this.hasNextDossiersPage()) return

    const { activePage, totalPages } = this.props
    this.loadingMore = true
    const result = this.props.fetchNextDossiersPage(activePage, totalPages, this.state.searchTerm)
    Promise.resolve(result).finally(() => {
      this.loadingMore = false
    })
  }

  hasNextDossiersPage = () => {
    const { activePage, totalPages } = this.props
    return Boolean(activePage && totalPages && activePage < totalPages)
  }

  bindMenuScroll = () => {
    this.unbindMenuScroll()

    setTimeout(() => {
      const dropdownNode = this.getDropdownNode()
      if (!dropdownNode) return

      this.menuElement = dropdownNode.querySelector('.menu.visible') || dropdownNode.querySelector('.menu.transition.visible')
      if (this.menuElement) {
        this.menuElement.addEventListener('scroll', this.handleMenuScroll, { passive: true })
      }
    }, 0)
  }

  unbindMenuScroll = () => {
    if (this.menuElement) {
      this.menuElement.removeEventListener('scroll', this.handleMenuScroll)
      this.menuElement = null
    }
  }

  getDropdownNode = () => {
    if (!this.dropdownRef?.current) return null
    return ReactDOM.findDOMNode(this.dropdownRef.current)
  }

  handleMenuScroll = (event) => {
    const target = event?.target
    if (!target) return

    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - this.scrollThreshold
    if (reachedBottom) this.handleLoadMoreDossiers()
  }

  handleSelectDossier = (event, props) => {
    this.setState({ loading: true, selection: 'dossier', selectedDossier: props.value })
    this.props.fetchProposedInvoice(props.value).then(id => {
      this.setState({ invoiceId: id, loading: false, fetchedInvoiceId: id })
    })
  }

  handleSelectEmpty = () => {
    this.setState({ selection: 'empty', invoiceId: this.props.entityId })
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    dossiers: getDossiers('createInvoiceForm')(state),
    activePage: dotProp.get(state, 'meta.dossier.keyWindows.byWindow.createInvoiceForm.page'),
    totalPages: dotProp.get(state, 'meta.dossier.keyWindows.byWindow.createInvoiceForm.totalPages')
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    fetchFirstDossiersPage: (filter = {}) => {
      dispatch(fetchEntities('dossiers', { name: 'createInvoiceForm', filter: filter, page: 1 }))
    },
    fetchNextDossiersPage: (activePage, totalPages, searchTerm = '') => {
      if (activePage && totalPages && activePage < totalPages) {
        return dispatch(fetchEntities('dossiers', {
          name: 'createInvoiceForm',
          filter: { search: searchTerm },
          page: activePage + 1,
          append: true
        }))
      }
      return Promise.resolve()
    },
    fetchProposedInvoice: (dossierId) => {
      return dispatch(getProposedInvoice(dossierId))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateInvoiceForm);
